import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Typography, Box, Divider, Link } from '@mui/material';
import { Components } from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
  sx?: any;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, sx }) => {
  const components: Components = {
    h1: ({ children }) => (
      <Typography 
        variant="h2" 
        component="h1" 
        gutterBottom 
        sx={{ color: 'white', mt: 2, mb: 3 }}
      >
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <>
        <Typography 
          variant="h5" 
          component="h2" 
          gutterBottom 
          sx={{ mt: 4, color: 'white' }}
        >
          {children}
        </Typography>
        <Divider sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />
      </>
    ),
    h3: ({ children }) => (
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ mt: 3, color: 'rgba(255, 255, 255, 0.9)' }}
      >
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography 
        variant="subtitle1" 
        gutterBottom 
        sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold' }}
      >
        {children}
      </Typography>
    ),
    p: ({ children }) => (
      <Typography 
        paragraph 
        sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
      >
        {children}
      </Typography>
    ),
    a: ({ href, children }) => (
      <Link 
        href={href} 
        sx={{ color: '#64b5f6' }}
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {children}
      </Link>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ paddingLeft: '20px', my: 2 }}>
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ paddingLeft: '20px', my: 2 }}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <li>
        <Typography 
          component="span" 
          sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
        >
          {children}
        </Typography>
      </li>
    ),
    strong: ({ children }) => (
      <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
        {children}
      </em>
    ),
    blockquote: ({ children }) => (
      <Box 
        component="blockquote" 
        sx={{ 
          borderLeft: '4px solid rgba(255, 255, 255, 0.3)',
          pl: 2,
          ml: 0,
          my: 2,
          color: 'rgba(255, 255, 255, 0.7)'
        }}
      >
        {children}
      </Box>
    ),
    code: ({ children }) => (
      <Box 
        component="code" 
        sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          padding: '2px 6px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
          color: 'rgba(255, 255, 255, 0.9)'
        }}
      >
        {children}
      </Box>
    ),
    pre: ({ children }) => (
      <Box 
        component="pre" 
        sx={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          padding: 2,
          borderRadius: 1,
          overflow: 'auto',
          my: 2,
          '& code': {
            backgroundColor: 'transparent',
            padding: 0,
            color: 'rgba(255, 255, 255, 0.9)'
          }
        }}
      >
        {children}
      </Box>
    ),
    hr: () => (
      <Divider sx={{ my: 3, bgcolor: 'rgba(255,255,255,0.2)' }} />
    ),
    img: ({ src, alt, ...props }: any) => {
      // Parse alt text for size directives (e.g., "alt text|width=800" or "alt text|50%" or "alt text|medium")
      const [actualAlt, sizeDirective] = (alt || '').split('|').map((s: string) => s.trim());
      
      let width = '100%';
      let maxWidth = '800px'; // Default max width
      
      if (sizeDirective) {
        if (sizeDirective.includes('width=')) {
          // Custom pixel width: |width=600
          const pixelWidth = sizeDirective.replace('width=', '');
          maxWidth = `${pixelWidth}px`;
        } else if (sizeDirective.includes('%')) {
          // Percentage width: |50%
          width = sizeDirective;
          maxWidth = '100%';
        } else if (sizeDirective === 'small') {
          maxWidth = '400px';
        } else if (sizeDirective === 'medium') {
          maxWidth = '600px';
        } else if (sizeDirective === 'large') {
          maxWidth = '900px';
        } else if (sizeDirective === 'full') {
          maxWidth = '100%';
        }
      }
      
      return (
        <Box
          component="img"
          src={src}
          alt={actualAlt || alt}
          loading="lazy"
          sx={{
            width,
            maxWidth,
            height: 'auto',
            display: 'block',
            margin: '2rem auto',
            borderRadius: 1,
            boxShadow: (theme) => 
              theme.palette.mode === 'dark' 
                ? '0 4px 6px rgba(0, 0, 0, 0.3)' 
                : '0 4px 6px rgba(0, 0, 0, 0.1)',
            // Responsive sizing
            '@media (max-width: 600px)': {
              maxWidth: sizeDirective?.includes('%') ? width : '100%',
              margin: '1rem auto',
            }
          }}
          {...props}
        />
      );
    },
  };

  return (
    <Box sx={sx}>
      <ReactMarkdown components={components}>
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;